/**
 * PublicShopMap — Google Maps view of all public shops (no auth).
 * Route: /map
 *
 * Features:
 * - Search bar with dropdown (filters by name/category/address)
 * - Custom markers with shop thumbnails
 * - Slide-up bottom panel (desktop: side drawer) with thumbnail slider + actions
 * - "View in 3D" navigates to viewer
 * - Viewport persisted to sessionStorage
 */

import { useEffect, useRef, useState, useMemo, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useShopStore } from '../../stores/shopStore'
import { apiUrl } from '../../config'
import { loadGoogleMaps } from '../../utils/loadGoogleMaps'
import { ShopReviewDrawer } from './ShopReviewDrawer'
import './PublicShopMap.css'

const GOOGLE_MAPS_API_KEY = import.meta.env.VITE_GOOGLE_MAPS_API_KEY || ''
const MAP_STATE_KEY = 'pubmap_viewport'

function toFiniteNumber(value: unknown): number | null {
  if (typeof value === 'number' && Number.isFinite(value)) return value
  if (typeof value === 'string') {
    const parsed = Number.parseFloat(value)
    if (Number.isFinite(parsed)) return parsed
  }
  return null
}

interface SavedViewport { lat: number; lng: number; zoom: number }

function saveViewport(v: SavedViewport) {
  try { sessionStorage.setItem(MAP_STATE_KEY, JSON.stringify(v)) } catch {}
}
function loadViewport(): SavedViewport | null {
  try {
    const raw = sessionStorage.getItem(MAP_STATE_KEY)
    if (!raw) return null
    const v = JSON.parse(raw)
    if (typeof v.lat === 'number' && typeof v.lng === 'number' && typeof v.zoom === 'number') return v
  } catch {}
  return null
}


type PublicShopEntry = ReturnType<typeof useShopStore.getState>['publicShops'][number]

interface ShopWithPos {
  shop: PublicShopEntry
  position: google.maps.LatLngLiteral
}

export default function PublicShopMap() {
  const navigate = useNavigate()
  const { t } = useTranslation('shops')
  const { publicShops, publicShopsLoading, fetchPublicShops } = useShopStore()

  const mapRef = useRef<HTMLDivElement>(null)
  const [map, setMap] = useState<google.maps.Map | null>(null)
  const [mapError, setMapError] = useState<string | null>(null)
  const [mapLoading, setMapLoading] = useState(true)
  const markersRef = useRef<Array<{ setMap: (m: google.maps.Map | null) => void }>>([])
  const didFitRef = useRef(false)

  // Search
  const [searchQuery, setSearchQuery] = useState('')
  const [searchFocused, setSearchFocused] = useState(false)
  const searchRef = useRef<HTMLInputElement>(null)

  // Selected shop panel
  const [selectedShop, setSelectedShop] = useState<PublicShopEntry | null>(null)
  const [panelThumbIndex, setPanelThumbIndex] = useState(0)
  const [reviewShopId, setReviewShopId] = useState<string | null>(null)

  // Fetch shops on mount
  useEffect(() => { fetchPublicShops() }, [])

  // Shops with valid location
  const shopsWithLocation = useMemo<ShopWithPos[]>(() =>
    publicShops
      .map((shop) => {
        const lat = toFiniteNumber((shop as any).location?.lat)
        const lng = toFiniteNumber((shop as any).location?.lng)
        if (lat === null || lng === null) return null
        return { shop, position: { lat, lng } }
      })
      .filter((e): e is ShopWithPos => e !== null),
    [publicShops]
  )

  // Search-filtered shops
  const searchResults = useMemo<ShopWithPos[]>(() => {
    if (!searchQuery.trim()) return []
    const q = searchQuery.toLowerCase()
    return shopsWithLocation.filter(({ shop }) =>
      shop.name?.toLowerCase().includes(q) ||
      shop.category?.toLowerCase().includes(q) ||
      (shop as any).address?.toLowerCase().includes(q)
    ).slice(0, 8)
  }, [searchQuery, shopsWithLocation])

  // Init map — runs once on mount only (fixes the "failed to load" on first visit bug)
  useEffect(() => {
    if (!mapRef.current) return
    if (!GOOGLE_MAPS_API_KEY) {
      setMapError(t('errors.mapsApiKeyNotConfigured'))
      setMapLoading(false)
      return
    }
    let cancelled = false
    ;(window as any).gm_authFailure = () => {
      if (!cancelled) { setMapError(t('errors.mapsApiKeyRestricted')); setMapLoading(false) }
    }
    const init = async () => {
      try {
        await loadGoogleMaps(GOOGLE_MAPS_API_KEY)
        if (cancelled || !mapRef.current) return
        const saved = loadViewport()
        const mapInstance = new google.maps.Map(mapRef.current, {
          center: saved ? { lat: saved.lat, lng: saved.lng } : { lat: 25.0762, lng: 55.1329 },
          zoom: saved ? saved.zoom : 12,
          mapTypeControl: false,
          streetViewControl: false,
          fullscreenControl: false,
          zoomControlOptions: { position: google.maps.ControlPosition.RIGHT_CENTER },
        })
        if (saved) didFitRef.current = true
        mapInstance.addListener('idle', () => {
          const c = mapInstance.getCenter()
          const z = mapInstance.getZoom()
          if (c && z !== undefined) saveViewport({ lat: c.lat(), lng: c.lng(), zoom: z })
        })
        // Close panel on map click
        mapInstance.addListener('click', () => setSelectedShop(null))
        if (!cancelled) { setMap(mapInstance); setMapLoading(false) }
      } catch {
        if (!cancelled) { setMapError(t('errors.failedToLoadMaps')); setMapLoading(false) }
      }
    }
    init()
    return () => {
      cancelled = true
      delete (window as any).gm_authFailure
    }
  }, []) // intentionally empty — run once on mount

  // Open shop from marker click or search result
  const openShop = useCallback((shop: PublicShopEntry, position?: google.maps.LatLngLiteral) => {
    setSelectedShop(shop)
    setPanelThumbIndex(0)
    if (map && position) {
      map.panTo(position)
      if (map.getZoom()! < 15) map.setZoom(15)
    }
  }, [map])

  // Place markers when map + shops are ready — uses thumbnail card overlays
  useEffect(() => {
    if (!map) return
    // Remove old markers/overlays
    markersRef.current.forEach((m) => m.setMap(null))
    markersRef.current = []
    if (shopsWithLocation.length === 0) return

    shopsWithLocation.forEach(({ shop, position }) => {
      const posterSrc = apiUrl(`/api/shops/${shop.id}/poster`)

      // Custom OverlayView: thumbnail card with shop name
      class ThumbMarker extends google.maps.OverlayView {
        private div: HTMLDivElement | null = null
        private pos: google.maps.LatLng
        private onClick: () => void

        constructor(pos: google.maps.LatLngLiteral, onClick: () => void) {
          super()
          this.pos = new google.maps.LatLng(pos.lat, pos.lng)
          this.onClick = onClick
        }

        onAdd() {
          const div = document.createElement('div')
          div.className = 'pub-map-thumb-marker'
          div.innerHTML = `
            <div class="pub-map-thumb-marker-img-wrap">
              <img src="${posterSrc}" alt="" draggable="false" />
              <span class="pub-map-thumb-marker-initial">${shop.name.charAt(0).toUpperCase()}</span>
            </div>
            <div class="pub-map-thumb-marker-label">${shop.name}</div>
            <div class="pub-map-thumb-marker-tail"></div>
          `
          div.addEventListener('click', (e) => { e.stopPropagation(); this.onClick() })
          this.div = div
          const panes = this.getPanes()!
          panes.overlayMouseTarget.appendChild(div)
        }

        draw() {
          if (!this.div) return
          const proj = this.getProjection()
          const point = proj.fromLatLngToDivPixel(this.pos)!
          this.div.style.left = `${point.x - 36}px`
          this.div.style.top = `${point.y - 76}px`
        }

        onRemove() {
          if (this.div) { this.div.parentNode?.removeChild(this.div); this.div = null }
        }

        // Implement setMap as required by OverlayView shape
        setMap(map: google.maps.Map | null) { super.setMap(map) }
      }

      const overlay = new ThumbMarker(position, () => openShop(shop, position))
      overlay.setMap(map)
      markersRef.current.push(overlay)
    })

    if (!didFitRef.current) {
      if (shopsWithLocation.length > 1) {
        const bounds = new google.maps.LatLngBounds()
        shopsWithLocation.forEach(({ position }) => bounds.extend(position))
        map.fitBounds(bounds, 50)
      } else {
        map.setCenter(shopsWithLocation[0].position)
        map.setZoom(15)
      }
    }
    didFitRef.current = true
  }, [map, shopsWithLocation, openShop])

  const handleCenterOnMe = () => {
    if (!map || !navigator.geolocation) return
    navigator.geolocation.getCurrentPosition(
      (pos) => { map.setCenter({ lat: pos.coords.latitude, lng: pos.coords.longitude }); map.setZoom(15) },
      (err) => alert(`Could not get location: ${err.message}`),
      { enableHighAccuracy: true, timeout: 10000 }
    )
  }

  const handleSearchSelect = (entry: ShopWithPos) => {
    setSearchQuery(entry.shop.name || '')
    setSearchFocused(false)
    openShop(entry.shop, entry.position)
  }

  const panelPosterUrl = selectedShop ? apiUrl(`/api/shops/${selectedShop.id}/poster`) : ''
  const hasPanel = !!selectedShop

  return (
    <div className="pub-map-page">
      <header className="pub-map-header">
        <button className="pub-map-back" onClick={() => navigate('/')}>
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            <polyline points="15 18 9 12 15 6"/>
          </svg>
          <span className="pub-map-back-label">{t('common:back')}</span>
        </button>

        {/* Search bar — inline in header */}
        <div className={`pub-map-search-wrap${searchFocused ? ' focused' : ''}`}>
          <div className="pub-map-search-inner">
            <svg className="pub-map-search-icon" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/>
            </svg>
            <input
              ref={searchRef}
              className="pub-map-search-input"
              type="text"
              placeholder="Search shops…"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onFocus={() => setSearchFocused(true)}
              onBlur={() => setTimeout(() => setSearchFocused(false), 150)}
            />
            {searchQuery && (
              <button className="pub-map-search-clear" onClick={() => { setSearchQuery(''); searchRef.current?.focus() }}>
                ×
              </button>
            )}
          </div>
          {searchFocused && searchResults.length > 0 && (
            <div className="pub-map-search-dropdown">
              {searchResults.map(({ shop, position }) => (
                <button
                  key={shop.id}
                  className="pub-map-search-item"
                  onMouseDown={() => handleSearchSelect({ shop, position })}
                >
                  <div className="pub-map-search-thumb">
                    <img src={apiUrl(`/api/shops/${shop.id}/poster`)} alt="" onError={(e) => { (e.target as HTMLImageElement).style.display = 'none' }} />
                  </div>
                  <div className="pub-map-search-text">
                    <span className="pub-map-search-name">{shop.name}</span>
                    {shop.category && <span className="pub-map-search-cat">{shop.category}</span>}
                    {(shop as any).address && <span className="pub-map-search-addr">{(shop as any).address}</span>}
                  </div>
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0, color: '#64748b' }}>
                    <polyline points="9 18 15 12 9 6"/>
                  </svg>
                </button>
              ))}
            </div>
          )}
        </div>

        <button className="pub-map-list-btn" onClick={() => navigate('/browse')}>
          <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
            <path d="M3 13h2v-2H3v2zm0 4h2v-2H3v2zm0-8h2V7H3v2zm4 4h14v-2H7v2zm0 4h14v-2H7v2zM7 7v2h14V7H7z"/>
          </svg>
          <span className="pub-map-list-label">Browse</span>
        </button>
      </header>

      {mapLoading && (
        <div className="pub-map-loading">
          <div className="pub-map-spinner" />
          <p>{publicShopsLoading ? t('loadingShops') : t('loadingMap')}</p>
        </div>
      )}

      {mapError && (
        <div className="pub-map-error">
          <p>⚠️ {mapError}</p>
          <button onClick={() => navigate('/')}>{t('common:backToHome')}</button>
        </div>
      )}

      {/* Map container always rendered so ref is stable */}
      <div
        ref={mapRef}
        className="pub-map-container"
        style={{ display: mapError ? 'none' : undefined }}
      />


      {/* Floating controls */}
      {map && !mapError && (
        <>
          <button className="pub-map-center-btn" onClick={handleCenterOnMe}>⊕ {t('nearMe')}</button>
          <div className="pub-map-info">
            {!publicShopsLoading && shopsWithLocation.length === 0
              ? t('noPublicShopsOnMap')
              : t('shopsOnMap', { count: shopsWithLocation.length })}
          </div>
        </>
      )}

      {/* Shop panel */}
      <div className={`pub-map-panel${hasPanel ? ' open' : ''}`} role="dialog" aria-modal="false">
        {selectedShop && (
          <>
            <div className="pub-map-panel-drag" />
            <div className="pub-map-panel-header">
              <div className="pub-map-panel-info">
                <h2 className="pub-map-panel-name">{selectedShop.name}</h2>
                <div className="pub-map-panel-meta">
                  {selectedShop.category && <span className="pub-map-panel-cat">{selectedShop.category}</span>}
                  {(selectedShop as any).address && (
                    <span className="pub-map-panel-addr">{(selectedShop as any).address}</span>
                  )}
                </div>
              </div>
              <button className="pub-map-panel-close" onClick={() => setSelectedShop(null)} aria-label="Close">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
                  <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
                </svg>
              </button>
            </div>

            {/* Thumbnail */}
            <div className="pub-map-panel-thumb-wrap">
              <img
                className="pub-map-panel-thumb"
                src={panelPosterUrl}
                alt={selectedShop.name}
                onError={(e) => { (e.target as HTMLImageElement).parentElement!.style.display = 'none' }}
              />
            </div>

            {/* Actions */}
            <div className="pub-map-panel-actions">
              <button
                className="pub-map-panel-btn primary"
                onClick={() => { window.location.href = `/?shopid=${selectedShop.id}` }}
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z"/>
                </svg>
                View in 3D
              </button>
              {(selectedShop as any).location?.lat && (selectedShop as any).location?.lng && (
                <a
                  className="pub-map-panel-btn secondary"
                  href={`https://www.google.com/maps/dir/?api=1&destination=${(selectedShop as any).location.lat},${(selectedShop as any).location.lng}`}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <polygon points="3 11 22 2 13 21 11 13 3 11"/>
                  </svg>
                  Directions
                </a>
              )}
              <button
                className="pub-map-panel-btn secondary"
                onClick={() => setReviewShopId(selectedShop.id)}
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/>
                </svg>
                Reviews
              </button>
            </div>
          </>
        )}
      </div>

      {/* Panel backdrop on mobile */}
      {hasPanel && <div className="pub-map-panel-backdrop" onClick={() => setSelectedShop(null)} />}

      {/* Review drawer */}
      {reviewShopId && (
        <ShopReviewDrawer
          shopId={reviewShopId}
          shopName={selectedShop?.name}
          onClose={() => setReviewShopId(null)}
        />
      )}
    </div>
  )
}
